import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { Icono } from '@xiltrion/ui/icono';
import { Spinner } from '@xiltrion/ui/spinner';
import { Button } from '@xiltrion/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@xiltrion/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@xiltrion/ui/toggle-group';
import type { Task } from '@/api/types';
import { Card, EmptyState, ErrorState, LoadingState, PageHeader } from '@/components/ui';
import { TaskRow } from '@/features/dashboard/DashboardPage';
import { dueGroup } from '@/features/cases/lib';
import type { SupportRequestRow } from '@pqrs/contracts';
import { supportKeys } from '@/features/support/api';
import { SupportRequestsView } from '@/features/support/SupportRequestsView';
import { SupportTaskRow, usePendingSupport } from '@/features/support/SupportTaskRow';
import { taskKeys, useTasks } from './api';

const GROUPS = [{ value: 'all', label: 'Todas' }, { value: 'overdue', label: 'Vencidas' }, { value: 'today', label: 'Vencen hoy' }, { value: 'tomorrow', label: 'Mañana' }, { value: 'blocked', label: 'Bloqueadas' }, { value: 'unassigned', label: 'Sin responsable' }];

/** Pestañas de «Mis tareas» (`?vista=`): las tareas (con los apoyos por responder) y los apoyos que pidió la persona. */
type View = 'tareas' | 'apoyos-enviados';

/** Una fila de la lista: una tarea del flujo o un apoyo que le piden a las áreas de la persona. */
type Row = { kind: 'task'; task: Task } | { kind: 'support'; support: SupportRequestRow };
const rowDue = (r: Row) => (r.kind === 'task' ? r.task.dueAt : r.support.case.dueAt);

/**
 * Mis tareas: las tareas agrupadas por vencimiento, junto con los apoyos que le piden a las áreas de la persona (se
 * responden como una tarea más, por el vencimiento del caso), y en otra pestaña los apoyos que ella pidió.
 */
export function TasksPage() {
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();
  const view: View = sp.get('vista') === 'apoyos-enviados' ? 'apoyos-enviados' : 'tareas';
  const tasksFetching = useIsFetching({ queryKey: taskKeys.all }) > 0;
  const supportFetching = useIsFetching({ queryKey: supportKeys.all }) > 0;
  const fetching = view === 'tareas' ? tasksFetching || supportFetching : supportFetching;
  const refresh = () => { if (view === 'tareas') void qc.invalidateQueries({ queryKey: taskKeys.all }); void qc.invalidateQueries({ queryKey: supportKeys.all }); };
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Mis tareas" subtitle={view === 'tareas' ? 'Lo que tienes por hacer en tus casos, también los apoyos que le piden a tus áreas.' : 'Los apoyos que pediste a otras áreas para responder una PQRS. No se envían al ciudadano.'}
        actions={<Button variant="ghost" size="icon" aria-label="Actualizar" onClick={refresh} disabled={fetching}>{fetching ? <Spinner /> : <Icono nombre='mdi:refresh' />}</Button>} />
      {/* Cambiar de pestaña limpia los filtros de la otra (grupo de tareas; estado y texto de los apoyos). */}
      <Tabs value={view} onValueChange={(v) => setSp(v === 'tareas' ? {} : { vista: v }, { replace: true })}>
        <TabsList variant="line">
          <TabsTrigger value="tareas" className="gap-2"><Icono nombre="mdi:check-circle-outline" /> Tareas</TabsTrigger>
          <TabsTrigger value="apoyos-enviados" className="gap-2"><Icono nombre="mdi:send-outline" /> Apoyos enviados</TabsTrigger>
        </TabsList>
      </Tabs>
      {view === 'tareas' ? <TaskList /> : <SupportRequestsView box="sent" />}
    </div>
  );
}

/**
 * Las tareas de la persona (o las sin responsable) y los apoyos por responder de sus áreas, agrupados por vencimiento
 * (el apoyo, por el del caso). «Bloqueadas» y «Sin responsable» son solo de tareas.
 */
function TaskList() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const group = sp.get('grupo') ?? 'all';
  const mine = useTasks({ mine: true, limit: 200 });
  const unassigned = useTasks({ limit: 200 });
  const support = usePendingSupport();
  const q = group === 'unassigned' ? unassigned : mine;
  const grouped = useMemo(() => {
    let list: Row[] = (q.data ?? []).map((task) => ({ kind: 'task', task }));
    if (group === 'unassigned') list = list.filter((r) => r.kind === 'task' && !r.task.assigneeUserId);
    else if (group === 'blocked') list = list.filter((r) => r.kind === 'task' && r.task.status === 'BLOCKED');
    else {
      list = [...list, ...(support.data ?? []).map((s): Row => ({ kind: 'support', support: s }))];
      if (group !== 'all') list = list.filter((r) => dueGroup(rowDue(r)).key === group);
    }
    const map = new Map<string, { label: string; order: number; items: Row[] }>();
    for (const r of list) { const g = dueGroup(rowDue(r)); const e = map.get(g.key) ?? { label: g.label, order: g.order, items: [] }; e.items.push(r); map.set(g.key, e); }
    return [...map.values()].sort((a, b) => a.order - b.order);
  }, [q.data, support.data, group]);
  const total = grouped.reduce((a, g) => a + g.items.length, 0);
  return (
    <div className="flex flex-col gap-4">
      <ToggleGroup type="single" variant="outline" size="sm" value={group} onValueChange={(v) => { if (v) setSp(v === 'all' ? {} : { grupo: v }, { replace: true }); }} aria-label="Filtrar tareas" className="flex-wrap">
        {GROUPS.map((g) => <ToggleGroupItem key={g.value} value={g.value}>{g.label}</ToggleGroupItem>)}
      </ToggleGroup>
      {q.isLoading ? <LoadingState rows={6} /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} title="No pudimos cargar tus tareas." /> : total === 0 ? (
        <EmptyState title={group === 'all' ? 'No tienes tareas pendientes.' : 'No hay tareas en este grupo.'} hint={group === 'all' ? 'Cuando recibas una nueva tarea aparecerá aquí.' : 'Prueba con otro grupo o vuelve a “Todas”.'} icon="mdi:check-decagram-outline" action={group !== 'all' ? <Button variant="outline" size="sm" onClick={() => setSp({}, { replace: true })}>Ver todas</Button> : undefined} />
      ) : grouped.map((g) => (
        <Card key={g.label} title={<span className="inline-flex items-center gap-2">{g.order <= 1 ? <Icono nombre="mdi:alert-circle" className="text-destructive" aria-hidden /> : g.order === 2 ? <Icono nombre="mdi:alert" className="text-warning" aria-hidden /> : null}{g.label} <span className="text-muted-foreground font-normal text-sm">({g.items.length})</span></span>}>
          <ul className="list-none m-0 p-0 divide-y divide-border">{g.items.map((r) => r.kind === 'task'
            ? <TaskRow key={r.task.id} task={r.task} onOpen={() => nav(`/tareas/${r.task.id}`)} />
            : <SupportTaskRow key={r.support.id} r={r.support} onOpen={() => nav(`/pqrs/${r.support.caseId}?tab=apoyos`)} />)}</ul>
        </Card>
      ))}
    </div>
  );
}
